import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import CasinoSidebar from '@/components/CasinoSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck, ChevronRight, Check, AlertTriangle, X, Star } from 'lucide-react';

export default function PreDepositChecklist() {
  const [casinoSlug, setCasinoSlug] = useState('');
  const [casinos, setCasinos] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/tools/casino-checklist-list').then(r => setCasinos(r.data.casinos)).catch(() => {}); }, []);

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!casinoSlug) { toast.error('Select a casino'); return; }
    setLoading(true);
    try {
      const res = await api.post(`/tools/pre-deposit-check?casino_slug=${casinoSlug}`);
      setResult(res.data);
    } catch (err) { toast.error('Check failed'); }
    finally { setLoading(false); }
  };

  const STATUS_ICON = { pass: { Icon: Check, color: '#10b981', bg: 'bg-[#10b981]/10' }, warning: { Icon: AlertTriangle, color: '#f59e0b', bg: 'bg-[#f59e0b]/10' }, fail: { Icon: X, color: '#ef4444', bg: 'bg-[#ef4444]/10' } };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="pre-deposit-checklist-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Play Smart</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Pre-Deposit Checklist</span>
      </nav>
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#10b981]/15 flex items-center justify-center shrink-0"><ShieldCheck className="h-6 w-6 text-[#10b981]" /></div>
        <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Pre-Deposit Checklist</h1><p className="text-[#a0a8b8] text-sm mt-1">Automated casino safety checker before depositing</p></div>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-[40%]">
              <div className="ciq-card">
                <form onSubmit={handleCheck} className="space-y-5">
                  <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Select Casino</label>
                    <Select value={casinoSlug} onValueChange={setCasinoSlug}>
                      <SelectTrigger className="h-11 bg-[#1a1f2e] border-white/10 text-white" data-testid="checklist-casino-select"><SelectValue placeholder="Choose a casino" /></SelectTrigger>
                      <SelectContent className="bg-[#252b3b] border-white/10 max-h-[300px]">{casinos.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}{c.is_partner ? ' \u2B50 Recommended' : ''}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#10b981] text-white font-medium text-sm btn-lift disabled:opacity-50" data-testid="checklist-generate-btn">
                    {loading ? 'Checking...' : 'Generate Safety Report'}
                  </button>
                </form>
              </div>
            </div>
            <div className="lg:w-[60%]">
              {result && result.found && (
                <div className="animate-slide-up space-y-4">
                  <div className={`ciq-card text-center border-${result.badge === 'safe' ? '[#10b981]' : result.badge === 'caution' ? '[#f59e0b]' : '[#ef4444]'}/30`}>
                    <p className="text-xs text-[#a0a8b8] uppercase mb-1">Trust Score</p>
                    <div className="relative w-24 h-24 mx-auto mb-3">
                      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 36 36">
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#252b3b" strokeWidth="3" />
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none"
                          stroke={result.trust_score >= 75 ? '#10b981' : result.trust_score >= 60 ? '#f59e0b' : '#ef4444'}
                          strokeWidth="3" strokeDasharray={`${result.trust_score}, 100`} strokeLinecap="round" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-white">{result.trust_score}</span>
                    </div>
                    <p className="text-white font-semibold">{result.casino_name}</p>
                    {result.is_partner && <span className="inline-flex items-center gap-1 px-2 py-0.5 mt-1 rounded-full bg-[#f59e0b]/10 text-[#f59e0b] text-xs font-medium"><Star className="h-3 w-3 fill-[#f59e0b]" />Recommended Partner</span>}
                    <p className={`text-sm font-medium mt-1 ${result.badge === 'safe' ? 'text-[#10b981]' : result.badge === 'caution' ? 'text-[#f59e0b]' : 'text-[#ef4444]'}`}>
                      {result.badge === 'safe' ? 'SAFE TO DEPOSIT' : result.badge === 'caution' ? 'PROCEED WITH CAUTION' : 'NOT RECOMMENDED'}
                    </p>
                  </div>
                  <div className="ciq-card">
                    <h3 className="text-white font-semibold text-sm mb-3">Safety Checks ({result.summary.pass + result.summary.warning + result.summary.fail} items)</h3>
                    <div className="space-y-2">
                      {result.checks.map((check, i) => {
                        const s = STATUS_ICON[check.status];
                        return (
                          <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${s.bg}`} data-testid={`check-item-${i}`}>
                            <s.Icon className="h-4 w-4 shrink-0" style={{ color: s.color }} />
                            <div className="flex-1"><p className="text-sm text-white font-medium">{check.name}</p><p className="text-xs text-[#a0a8b8]">{check.detail}</p></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {result.strengths.length > 0 && (
                    <div className="ciq-card"><h3 className="text-white font-semibold text-sm mb-2">Strengths</h3>
                      <ul className="space-y-1.5">{result.strengths.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#a0a8b8]"><Check className="h-3.5 w-3.5 text-[#10b981] shrink-0 mt-0.5" />{s}</li>)}</ul>
                    </div>
                  )}
                  {result.concerns.length > 0 && (
                    <div className="ciq-card"><h3 className="text-white font-semibold text-sm mb-2">Concerns</h3>
                      <ul className="space-y-1.5">{result.concerns.map((c, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#a0a8b8]"><AlertTriangle className="h-3.5 w-3.5 text-[#f59e0b] shrink-0 mt-0.5" />{c}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
              {result && !result.found && <div className="ciq-card text-center py-8"><p className="text-[#f59e0b] text-sm">{result.message}</p></div>}
              {!result && <div className="ciq-card flex flex-col items-center justify-center py-12 text-center"><ShieldCheck className="h-10 w-10 text-[#3a4155] mb-3" /><p className="text-[#64748b] text-sm">Select a casino to generate a safety report</p></div>}
            </div>
          </div>
        </div>
        <CasinoSidebar sourcePage="pre-deposit-checklist" />
      </div>
    </div>
  );
}
